import React, { useState, useRef, useEffect } from 'react';
import {
  UserPlus,
  Mail,
  Phone,
  Building,
  User,
  Linkedin,
  Shield,
  RotateCcw,
} from 'lucide-react';
import {
  useSubmitMembershipForm,
  MembershipFormData,
  sendOtp,
  SendOtpResponse,
} from '../services';

interface MembershipFormProps {
  onSuccess: () => void;
}

const MembershipForm: React.FC<MembershipFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState<MembershipFormData>({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    linkedinProfile: '',
  });

  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [receivedOtp, setReceivedOtp] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    submitForm,
    isSubmitting,
    error: submitError,
  } = useSubmitMembershipForm();

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const validateField = (name: string, value: string): string | null => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return `${
        name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1')
      } is required`;
    }

    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        errors[key] = error;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!formData.email.trim()) {
      setOtpError('Please enter your email address first');
      return;
    }

    try {
      setIsOtpLoading(true);
      setOtpError(null);

      const response: SendOtpResponse = await sendOtp(formData.email);

      if (response.succeeded && response.data.otp) {
        setReceivedOtp(response.data.otp);
        setOtpStep(true);
        startResendCooldown();
      } else {
        setOtpError(
          response.data.message || 'Failed to send OTP. Please try again.'
        );
      }
    } catch (error) {
      setOtpError('Error sending OTP. Please try again.');
      console.error('OTP sending error:', error);
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    await handleSendOtp();
  };

  const startResendCooldown = () => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setResendCooldown(60);
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerifyOtp = () => {
    const trimmedOtp = otp.trim();
    if (!trimmedOtp) {
      setOtpError('Please enter the OTP');
      return false;
    }

    if (trimmedOtp === receivedOtp.trim()) {
      setOtpError(null);
      return true;
    } else {
      setOtpError('Invalid OTP. Please check and try again.');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpStep) {
      // First step: Validate form and send OTP
      if (!validateForm()) {
        return;
      }
      await handleSendOtp();
      return;
    }

    // Second step: Verify OTP and submit form
    if (!handleVerifyOtp()) {
      return;
    }

    // Trim all form data before submission
    const trimmedFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [key, value.trim()])
    ) as MembershipFormData;

    const result = await submitForm(trimmedFormData);
    if (result.success) {
      onSuccess();
    }
  };

  const handleBackToForm = () => {
    setOtpStep(false);
    setOtp('');
    setOtpError(null);
    setReceivedOtp('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      {/* Error message */}
      {submitError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {submitError}
        </div>
      )}

      {!otpStep ? (
        // Form fields step
        <>
          {/* Two-column grid for desktop, single column for mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm sm:text-base font-medium mb-2 text-secondary-900">
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-primary-500" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base ${
                    validationErrors.fullName
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>
              {validationErrors.fullName && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.fullName}
                </p>
              )}
            </div>

            {/* Email ID */}
            <div>
              <label className="block text-sm sm:text-base font-medium mb-2 text-secondary-900">
                Email ID *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-primary-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base ${
                    validationErrors.email
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="Enter your email address"
                />
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.email}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm sm:text-base font-medium mb-2 text-secondary-900">
                Phone Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-primary-500" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base ${
                    validationErrors.phone
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="Enter your phone number"
                />
              </div>
              {validationErrors.phone && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.phone}
                </p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm sm:text-base font-medium mb-2 text-secondary-900">
                Company Name *
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-primary-500" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base ${
                    validationErrors.companyName
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-primary-500 focus:ring-primary-500'
                  }`}
                  placeholder="Enter your company name"
                />
              </div>
              {validationErrors.companyName && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.companyName}
                </p>
              )}
            </div>
          </div>

          {/* LinkedIn Profile - Full width */}
          <div>
            <label className="block text-sm sm:text-base font-medium mb-2 text-secondary-900">
              LinkedIn Profile URL *
            </label>
            <div className="relative">
              <Linkedin className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-primary-500" />
              <input
                type="url"
                name="linkedinProfile"
                value={formData.linkedinProfile}
                onChange={handleInputChange}
                required
                className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base ${
                  validationErrors.linkedinProfile
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-primary-500 focus:ring-primary-500'
                }`}
                placeholder="Enter your LinkedIn profile URL"
              />
            </div>
            {validationErrors.linkedinProfile && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.linkedinProfile}
              </p>
            )}
          </div>

          {/* Send OTP Button */}
          <div className="text-center mt-6">
            <button
              type="submit"
              disabled={isOtpLoading}
              className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 text-white text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 cursor-pointer hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 bg-primary-500"
            >
              {isOtpLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                  <span className="text-sm sm:text-base">Sending OTP...</span>
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="text-sm sm:text-base">Send OTP</span>
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        // OTP verification step
        <>
          <div className="text-center mb-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 text-primary-500">
              Verify Your Email
            </h3>
            <p className="text-sm sm:text-base text-secondary-700 mb-2">
              We've sent a verification code to{' '}
              <span className="font-medium text-primary-500">
                {formData.email}
              </span>
            </p>
            <p className="text-xs sm:text-sm text-secondary-600">
              📧 Please check your mail inbox and if not found, then check in
              spam folder
            </p>
          </div>

          {/* OTP Input */}
          <div>
            <label className="block text-sm sm:text-base font-medium mb-2 text-secondary-900">
              Enter OTP *
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-primary-500" />
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                maxLength={6}
                className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base text-center tracking-widest ${
                  otpError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-primary-500 focus:ring-primary-500'
                }`}
                placeholder="Enter 6-digit OTP"
              />
            </div>
            {otpError && (
              <p className="mt-2 text-sm text-red-600">{otpError}</p>
            )}
          </div>

          {/* Resend OTP */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || isOtpLoading}
              className="inline-flex items-center text-sm text-primary-500 hover:opacity-80 transition-opacity duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isOtpLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
                  Sending OTP...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {resendCooldown > 0
                    ? `Resend OTP in ${resendCooldown}s`
                    : 'Resend OTP'}
                </>
              )}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={handleBackToForm}
              className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-primary-500 border border-primary-500 text-base sm:text-lg font-medium rounded-lg transition-colors duration-200 cursor-pointer hover:bg-primary-50"
            >
              Back to Form
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !otp}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-white text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 cursor-pointer hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 bg-primary-500"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                  <span className="text-sm sm:text-base">Submitting...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="text-sm sm:text-base">
                    Complete Registration
                  </span>
                </>
              )}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default MembershipForm;
